/**
 * Category Page Wrapper for ISR
 *
 * This component uses STATIC IMPORTS for optimal ISR performance.
 * - No dynamic() imports - all components are bundled together
 * - No LazyLoadBlock - full content is pre-rendered in HTML
 * - Components are imported directly for faster hydration
 * - All SEO-critical data is pre-fetched on server (hashtags, products, etc.)
 *
 * The old pages (category.desktop.page.tsx, category.mobile.page.tsx) are DEPRECATED.
 */

'use client';

import React, {useEffect, useMemo, useState} from 'react';
import dynamic from 'next/dynamic';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import NotFound from '@/_application/core/components/ui/not-found';
import useConversionApi from '@/_application/modules/tracking/meta/tracking-conversion-api';
import {MetaEventList} from '@/_application/modules/tracking/meta/model/meta-conversion';
import {Aggregation, CategoryTree, Customer, SortFields} from '__generated__/__types__';
import {useAtom} from 'jotai';
import {pageNameServerSideAtom} from '@/store/mobile-layout/mobile-layout';
import {Element} from 'react-scroll';
import {
    getUniqueAggregation,
    getValidAggregationList,
    getValidSortField,
} from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter/filter/helper/attributes';
import {filterValidHashtags} from '@/framework/server-side/utils';
import {useIsrPageInit} from '@/_application/pages/_isr';

// ============================================
// STATIC IMPORTS - Desktop Components (above-fold)
// ============================================
import ExpandDescription from '@/_application/core/components/commons/expand-html-content/expand-description';
import PopularKeywords from '@/_application/core/components/commons/popular-keywords/popular-keywords';
import CategoryBannerCollection from '@/_application/modules/categories/components/category-banner-collection';
import CategoryBreadCrumbs from '@/_application/modules/categories/components/bread-crumbs/category-bread-crumbs';
import CategoryBannerSwiper from '@/_application/modules/categories/components/category-banner-swiper';
import CategoryBrandList from '@/_application/modules/categories/components/category-brand-list';
import CategoryProducts from '@/_application/modules/categories/components/category-products';
import HotSearchKeyWords from '@/_application/modules/categories/components/hot-search-keywords/hot-search-keywords';
import SubCategoryList from '@/_application/modules/categories/components/sub-category-list/sub-category-list';
import CategoryPageLayout from '@/_application/modules/categories/layouts/category-page.layout';
import {ProductListViewProvider} from '@/_application/modules/products/provider/product-list-view.provider';

// Below-fold — dynamic imports with ISR (HTML pre-rendered, JS code-split)
const ProductsSuggestionBlock = dynamic(
    () => import('@/_application/modules/products/components/products-suggestion/products-suggestion-block'),
    {ssr: true},
);
const ViewedProducts = dynamic(
    () => import('@/_application/modules/products/components/viewed-products/viewed-products'),
    {ssr: true},
);
const UsefulInformation = dynamic(
    () => import('@/_application/modules/useful-information/useful-info.desktop'),
    {ssr: true},
);

// ============================================
// STATIC IMPORTS - Mobile Components (above-fold)
// ============================================
import CategoryBannerMobile from '@/_application/modules/categories/components/category-banner.mobile';
import SortingFilterToolbar from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter-toolbar';
import SubCategoryListMobile from '@/_application/modules/categories/components/sub-category-list/sub-category-list.mobile';
import {ProductCardListMobile} from '@/_application/modules/products/components/product-card-list.mobile';
import {SmallBrandSwiperCategory} from '@/_application/pages/category/components/small-brand-swiper-category.mobile';
import {CouponWidgetContent} from '@/nova/modules/sales-rule';

// Below-fold mobile — dynamic with ISR
const UsefulInformationMobile = dynamic(
    () => import('@/_application/modules/useful-information/useful-info.mobile'),
    {ssr: true},
);

export interface CategoryPageWrapperProps {
    variables?: DynamicPageServerSideProps['variables'];
    isMobile?: boolean;
    layout?: DynamicPageServerSideProps['layout'];
}

/**
 * Desktop Category Page for ISR
 * All components are STATICALLY imported - no dynamic(), no LazyLoadBlock
 * Hashtags are pre-fetched on server for SEO
 * Note: categoryId validation is done at wrapper level
 */
function CategoryPageDesktopIsr({
    variables,
    layout,
    customer,
}: Readonly<DynamicPageServerSideProps & {customer?: Customer | null}>) {
    const {categoryDetail: categoryDetailSsr, productListItemsQueryOptions, initialData} = variables;
    const {categoryBanners, categoryBannerCollection, categoryHashtags} = initialData ?? {};
    const categoryDetail = categoryDetailSsr?.categoryList ?? [];
    const categoryId = variables?.categoryId!; // categoryId is validated at wrapper level
    const categoryUrl = variables?.types?.type;
    const {sendProcessedDataToConversionAPI} = useConversionApi();

    // Hashtags are pre-fetched from server for SEO - use utility function
    const hashTags = useMemo(() => filterValidHashtags(categoryHashtags), [categoryHashtags]);

    const categoryCouponFilters = useMemo(
        () => ({
            voucher: {category: {eq: String(categoryId)}},
            eWalletVoucher: {
                category: {eq: String(categoryId)},
                partner: {eq: '*'},
            },
        }),
        [categoryId],
    );

    // Only tracking runs on client (not SEO-critical)
    useEffect(() => {
        if (customer) {
            sendProcessedDataToConversionAPI(MetaEventList.VIEW_CATEGORY, categoryDetail.at(0)?.name, customer);
        }
    }, [customer, categoryDetail, sendProcessedDataToConversionAPI]);

    return (
        <CategoryPageLayout layout={layout} variables={variables}>
            <div className="flex flex-col items-center">
                <div className="w-full px-5 pt-5 lg:w-[1024px] xl:w-[1204px] xl:px-0">
                    <CategoryBreadCrumbs
                        breadCrumbItems={variables?.initialData?.breadCrumbs ?? []}
                        categoryName={categoryDetail?.at(0)?.name!}
                        categoryUrl={categoryUrl}
                    />
                    <CategoryBannerSwiper categoryBanners={categoryBanners} categoryId={categoryId} />
                    <SubCategoryList categories={(categoryDetail?.at(0)?.children ?? []) as CategoryTree[]} />
                    <CategoryBrandList variables={variables} />
                    <HotSearchKeyWords categoryId={categoryId} />
                    <CouponWidgetContent
                        className="w-full"
                        variant="voucher"
                        filter={categoryCouponFilters.voucher}
                    />
                    <CouponWidgetContent
                        className="mt-5 w-full"
                        variant="eWalletVoucher"
                        filter={categoryCouponFilters.eWalletVoucher}
                    />
                    <CategoryBannerCollection categoryBanners={categoryBannerCollection ?? []} />
                    <ProductListViewProvider layout={layout} variables={variables}>
                        <CategoryProducts
                            categoryName={categoryDetail?.at(0)?.name ?? ''}
                            initialProducts={variables.products?.products}
                            productListItemsQueryOptions={productListItemsQueryOptions}
                        />
                    </ProductListViewProvider>
                </div>
                <div className="w-full">
                    <ProductsSuggestionBlock />
                </div>
                <div className="w-full">
                    <ViewedProducts />
                </div>
                <div className="w-full lg:w-[1024px] xl:w-[1204px]">
                    <ExpandDescription htmlDescription={categoryDetail?.at(0)?.description ?? ''} />
                    <UsefulInformation className="my-5 rounded-[20px] bg-white p-5 pb-0" categoryId={categoryId} />
                    <PopularKeywords hashTags={hashTags} className="mb-5" />
                </div>
            </div>
        </CategoryPageLayout>
    );
}

/**
 * Mobile Category Page for ISR
 * All components are STATICALLY imported - no dynamic(), no LazyLoadBlock
 * Hashtags are pre-fetched on server for SEO
 * Note: categoryId validation is done at wrapper level
 */
function CategoryPageMobileIsr({
    variables,
    layout,
    customer,
}: Readonly<DynamicPageServerSideProps & {customer?: Customer | null}>) {
    const {categoryDetail: categoryDetailSsr, products, productListItemsQueryOptions, initialData} = variables;
    const currentPage = variables?.types?.page ?? '';
    const {categoryBanners, categoryBannerCollection, categoryHashtags} = initialData ?? {};
    const categoryDetail = categoryDetailSsr?.categoryList ?? [];
    const {aggregations, sort_fields: sortFields} = products?.products ?? {};
    const [, setPageNameServerSide] = useAtom(pageNameServerSideAtom);
    const categoryId = variables?.categoryId!; // categoryId is validated at wrapper level
    const {sendProcessedDataToConversionAPI} = useConversionApi();
    const [isScrolledToLastProduct, setIsScrolledToLastProduct] = useState<boolean>(false);

    // Hashtags are pre-fetched from server for SEO - use utility function
    const validHashtag = useMemo(() => filterValidHashtags(categoryHashtags), [categoryHashtags]);

    // Only tracking and page name setup runs on client (not SEO-critical)
    useEffect(() => {
        setPageNameServerSide(variables?.categoryDetail?.categoryList?.at(0)?.name!);

        if (customer) {
            sendProcessedDataToConversionAPI(MetaEventList.VIEW_CATEGORY, categoryDetail.at(0)?.name, customer);
        }

        return () => {
            setPageNameServerSide('');
        };
    }, [
        customer,
        categoryDetail,
        sendProcessedDataToConversionAPI,
        setPageNameServerSide,
        variables?.categoryDetail?.categoryList,
    ]);

    const handleScrollToLastProduct = () => {
        setIsScrolledToLastProduct(true);
    };

    const validSortFields: SortFields = useMemo(() => {
        return getValidSortField(sortFields ?? {});
    }, [sortFields]);

    const validAggregationList = useMemo(() => {
        const uniqueAggregations = getUniqueAggregation((aggregations ?? []) as Aggregation[]);
        return getValidAggregationList(uniqueAggregations, currentPage);
    }, [currentPage, aggregations]);

    const categoryCouponFilters = useMemo(
        () => ({
            voucher: {category: {eq: String(categoryId)}},
            eWalletVoucher: {
                category: {eq: String(categoryId)},
                partner: {eq: '*'},
            },
        }),
        [categoryId],
    );

    return (
        <ProductListViewProvider layout={layout} variables={variables}>
            <div className="active fixed top-16 z-20 shadow-md">
                <SortingFilterToolbar aggregations={validAggregationList} sortFields={validSortFields} />
            </div>
            <div className="my-10 pt-6">
                <div className="bg-white">
                    {categoryBanners && categoryBanners.length > 0 && (
                        <CategoryBannerMobile categoryBanners={categoryBanners} categoryId={categoryId} />
                    )}
                    <div className="mx-auto h-1 w-[92vw] border-t border-gray-200 bg-white"></div>
                    <SmallBrandSwiperCategory variables={variables} />
                </div>

                <SubCategoryListMobile categories={(categoryDetail?.at(0)?.children ?? []) as CategoryTree[]} />

                <CouponWidgetContent
                    className="mt-4 w-full"
                    variant="voucher"
                    filter={categoryCouponFilters.voucher}
                    isMobile={true}
                />
                <CouponWidgetContent
                    className="mt-4 w-full"
                    variant="eWalletVoucher"
                    filter={categoryCouponFilters.eWalletVoucher}
                    isMobile={true}
                />

                <div className="mt-4">
                    <div className="mb-4 bg-white">
                        <p className="ml-4 pt-3 text-base font-bold text-primary">Bộ sưu tập</p>
                        {categoryBannerCollection && categoryBannerCollection.length > 0 && (
                            <CategoryBannerMobile categoryBanners={categoryBannerCollection} categoryId={categoryId} />
                        )}
                    </div>
                    <Element
                        name="grid"
                        className="flex border-solid border-border-200 border-opacity-70 bg-gradient-to-b to-gray-100">
                        <ProductCardListMobile
                            initialProducts={variables.products?.products}
                            productListItemsQueryOptions={productListItemsQueryOptions}
                            handleScrollToLastProduct={handleScrollToLastProduct}
                        />
                    </Element>
                </div>

                {isScrolledToLastProduct && (
                    <>
                        <ExpandDescription htmlDescription={categoryDetail?.at(0)?.description ?? ''} />
                        <div className="mt-4">
                            <UsefulInformationMobile className="w-full" categoryId={categoryId} />
                        </div>
                        {validHashtag && <PopularKeywords hashTags={validHashtag} />}
                    </>
                )}
            </div>
        </ProductListViewProvider>
    );
}

/**
 * Category Page Wrapper for ISR
 * Entry point that selects mobile or desktop version
 * Uses centralized useIsrPageInit to handle storeServerData
 */
export const CategoryPageWrapper: React.FC<CategoryPageWrapperProps> = ({variables, isMobile, layout}) => {
    // Initialize ISR page - handles storeServerData and returns customer
    const customer = useIsrPageInit(variables);
    const categoryId = variables?.categoryId;

    if (!variables) {
        return null;
    }

    // Safety check - if categoryId is missing, show NotFound
    // This should not happen in normal flow as getStaticProps validates urlResolver.id
    if (!categoryId) {
        return <NotFound />;
    }

    if (isMobile) {
        return <CategoryPageMobileIsr variables={variables} layout={layout} customer={customer} />;
    }

    return <CategoryPageDesktopIsr variables={variables} layout={layout} customer={customer} />;
};

export default CategoryPageWrapper;
